import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Dot, LikeCounter } from '@xing-com/crate-social-utils';
import type { LikeCounterProps } from '@xing-com/crate-social-utils';
import { useFeatureSwitch } from '@xing-com/hub';
import { motionTimeNumberXS, motionTimeNumberS } from '@xing-com/tokens';

import { featureSwitches } from '../../constants';
import { CommentCounter } from '../comment-counter/comment-counter';
import type { CommentCounterProps } from '../comment-counter/comment-counter';
import type { ShareCounterProps } from '../share-counter/share-counter';
import { ShareCounter } from '../share-counter/share-counter';
import { ViewCounter } from '../view-counter/view-counter';
import type { ViewCounterProps } from '../view-counter/view-counter';

import { Styled, socialCountersTransitionName } from './social-counters.style';

export type SocialCountersProps = {
  className?: string;
  comments?: CommentCounterProps;
  compact?: boolean;
  likes?: LikeCounterProps;
  shares?: ShareCounterProps;
  views?: ViewCounterProps & { hasViewCounter: boolean };
};

export const SocialCounters: React.FC<SocialCountersProps> = ({
  className,
  comments = { counter: 0 },
  compact = false,
  likes = { counter: 0, urn: '', propChannel: '' },
  shares = { counter: 0 },
  views = { hasViewCounter: false, urn: '' },
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const hasLikes = !!likes.counter;
  const hasComments = !!comments.counter;
  const hasShares = !!shares.counter;
  const hasViews = views.hasViewCounter;
  const hasViewCounter = useFeatureSwitch(featureSwitches.viewCounter);
  const hasCounters = hasLikes || hasComments || hasShares || hasViews;

  const Dot1 = !compact && hasLikes && (hasComments || hasShares);
  const Dot2 = !compact && hasComments && hasShares;

  return (
    <CSSTransition
      appear={hasCounters}
      classNames={socialCountersTransitionName}
      in={hasCounters}
      mountOnEnter
      timeout={{
        appear: 40,
        enter: parseInt(motionTimeNumberXS),
        exit: parseInt(motionTimeNumberS),
      }}
      unmountOnExit
      nodeRef={wrapperRef}
    >
      <Styled.SocialCountersWrapper
        className={className}
        data-testid="social-counters"
        ref={wrapperRef}
      >
        <Styled.SocialCounters $compact={compact}>
          <LikeCounter {...likes} socialBarHasCounters={hasCounters} />
          {Dot1 && <Dot margin="small" isGrey />}
          {hasComments && <CommentCounter {...{ ...comments, compact }} />}
          {Dot2 && <Dot margin="small" isGrey />}
          {hasShares && (
            <ShareCounter {...{ counter: shares.counter, compact }} />
          )}
        </Styled.SocialCounters>
        {hasViews && hasViewCounter ? <ViewCounter {...views} /> : null}
      </Styled.SocialCountersWrapper>
    </CSSTransition>
  );
};
